/**
 * WAF SSL certificates — read-only list + filter catalog, scoped by the Owl
 * service path. Backed by `GET /services/:service_id/waf/ssl/certs` (paginated) and
 * `GET /services/:service_id/waf/ssl/options` (filter option lists). Mutations (create/delete/verify)
 * are a later slice.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import {
  serviceApiV3,
  wafKeys,
  unwrap,
  unwrapList,
  type ListResult,
} from './_shared';

/** One SSL certificate (json mirrors BE `providers.SSLCert`). Timestamps are
 *  int64 unix seconds. `type` is a vendor token (e.g. `akamai_cps_third_party`). */
export interface WafSslCert {
  id: number;
  type: string;
  state?: string;
  status?: string;
  binding_state?: string;
  /** "setup" = the cert still needs DNS/HTTP DCV setup; "" = none. */
  chip?: string;
  bindings: string[];
  cn: string;
  san: string[];
  issuer?: string;
  serial_number?: string;
  signature_algorithm?: string;
  key_algorithm?: string;
  deletable: boolean;
  not_before?: number;
  not_after?: number;
  created_at?: number;
  updated_at?: number;
}

/** One `{ value, label }` filter option. */
export interface WafSslOption {
  value: string;
  label: string;
}

/** Static filter catalog (`GET /waf/ssl/options`). */
export interface WafSslFilterOptions {
  types: WafSslOption[];
  statuses: WafSslOption[];
  chips: WafSslOption[];
  search_fields: WafSslOption[];
}

/** One ACME domain-control challenge (dns-01 / http-01). */
export interface WafSslChallenge {
  type?: string;
  name: string;
  value: string;
}

/** A generic name/value target (DNS delegation, HTTP redirect). */
export interface WafSslNameValue {
  name: string;
  value: string;
}

/** Per-bound-domain DCV info (`GET /waf/ssl/certs/:id/verification`). */
export interface WafSslDomainVerification {
  domain: string;
  caa: unknown[];
  dns: { challenge: WafSslChallenge; ns: WafSslNameValue; cname: WafSslNameValue };
  http: { challenge: WafSslChallenge; redirect: WafSslNameValue };
}

export interface WafSslListFilters {
  page?: number;
  page_size?: number;
  /** Search keyword, scoped by `search_by`. */
  search?: string;
  /** Search field: `cn` (default) | `domain` | `fqdn`. */
  search_by?: string;
  /** Canonical type token (e.g. `zero_ssl`, `akamai`). */
  type?: string;
  /** Status token (e.g. `active`, `issuing`). */
  status?: string;
  /** Chip filter, e.g. `setup` (needs DCV setup). */
  chip?: string;
  /** Expiry window — unix seconds; `not_after <= expires_before`. */
  expires_before?: number;
  /** Expiry window — unix seconds; `not_after >= expires_after`. */
  expires_after?: number;
  sort?: string;
  order?: string;
}

/** List a service's SSL certificates (paginated, server-side filtered). */
export function useWafSslCerts(
  serviceId: string | undefined,
  filters: WafSslListFilters = {},
) {
  const params = { ...filters };
  return useQuery<ListResult<WafSslCert>>({
    queryKey: wafKeys.ssl(serviceId ?? '', filters as Record<string, unknown>),
    enabled: !!serviceId,
    staleTime: 15_000,
    queryFn: async () => unwrapList<WafSslCert>(await serviceApiV3.get(`/services/${serviceId}/waf/ssl/certs`, { params })),
  });
}

/** The SSL filter option catalog (types/statuses/chips/search fields). */
export function useWafSslOptions(serviceId: string | undefined) {
  return useQuery<WafSslFilterOptions>({
    queryKey: wafKeys.sslOptions(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 5 * 60_000,
    queryFn: async () =>
      unwrap<WafSslFilterOptions>(
        await serviceApiV3.get(`/services/${serviceId}/waf/ssl/options`),
      ),
  });
}

/**
 * Delete an SSL certificate (`DELETE /waf/ssl/certs/:id`). The vendor `type` (the
 * cert's `type` token from the list row) is required by the backend.
 */
export function useDeleteWafSslCert(
  serviceId: string | undefined,
  options?: UseMutationOptions<void, Error, { id: number; type: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, type }: { id: number; type: string }) => {
      await serviceApiV3.delete(`/services/${serviceId}/waf/ssl/certs/${id}`, {
        params: { type },
      });
    },
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'ssl'] });
      options?.onSuccess?.(...args);
    },
  });
}

/**
 * The ZeroSSL/managed DCV challenges for a cert pending validation
 * (`GET /waf/ssl/certs/:id/verification`). Fetched lazily — pass `enabled=true`
 * only while the verification dialog is open.
 */
export function useWafSslVerification(
  serviceId: string | undefined,
  certId: number | undefined,
  enabled: boolean,
) {
  return useQuery<WafSslDomainVerification[]>({
    queryKey: wafKeys.sslVerification(serviceId ?? '', certId ?? 0),
    enabled: !!serviceId && !!certId && enabled,
    queryFn: async () =>
      unwrapList<WafSslDomainVerification>(
        await serviceApiV3.get(`/services/${serviceId}/waf/ssl/certs/${certId}/verification`),
      ).items,
  });
}

/** Re-check ("verify now") a cert's DCV challenges (`PUT /waf/ssl/certs/:id/verification`). */
export function useTriggerWafSslVerification(
  serviceId: string | undefined,
  certId: number | undefined,
  options?: UseMutationOptions<void, Error, void>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await serviceApiV3.put(`/services/${serviceId}/waf/ssl/certs/${certId}/verification`, null);
    },
    ...options,
    onSuccess: (...args) => {
      if (serviceId && certId) {
        qc.invalidateQueries({ queryKey: wafKeys.sslVerification(serviceId, certId) });
        qc.invalidateQueries({ queryKey: ['waf', serviceId, 'ssl'] });
      }
      options?.onSuccess?.(...args);
    },
  });
}
